/**
 * Formats a date string to a short format with timezone support
 * Format: MMM DD, YYYY HH:MM:SS AM/PM
 * @param dateString - The date string to format
 * @param timezone - The timezone to format the date in (defaults to user's local timezone)
 * @returns Formatted date string or "No timestamp" for null/invalid dates
 */
export const formatDateShort = (
  dateString: string | undefined,
  timezone?: string
): string => {
  if (!dateString) return "No timestamp";

  try {
    const date = new Date(dateString);

    // Check if date is invalid
    if (isNaN(date.getTime())) {
      return "No timestamp";
    }

    // Check if date is Jan 1, 0001 (common default/null date)
    if (
      date.getFullYear() === 1 &&
      date.getMonth() === 0 &&
      date.getDate() === 1
    ) {
      return "No timestamp";
    }

    // Use provided timezone or default to user's local timezone
    const timeZone =
      timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Format the date
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    return formatter.format(date);
  } catch (error) {
    return "No timestamp";
  }
};

/**
 * Gets the user's timezone from localStorage or defaults to browser timezone
 */
export const getUserTimezone = (): string => {
  try {
    const storedSettings = localStorage.getItem("userSettings");
    if (storedSettings) {
      const settings = JSON.parse(storedSettings);
      return (
        settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      );
    }
  } catch (error) {
    console.warn("Failed to get user timezone from localStorage:", error);
  }

  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};
